import React, { useEffect, useMemo, useState } from "react";
import { Target, Clock, Trophy, Phone as PhoneIcon, Fire } from "@phosphor-icons/react";
import CallTimer from "./CallTimer";

/**
 * SessionHUD — barre sticky en tête du portail téléprospection.
 *
 * Toujours visible pendant le mode "appels" :
 *   - Compteur X/Y appels traités (avec % progress)
 *   - Rappels programmés du jour
 *   - Ventes du jour (nombre + confetti si > 0)
 *   - Compteur "appels/h" (rafraîchi live à partir des timestamps de sessions)
 *   - Minuteur d'appel en cours (visible dès que startAt est set)
 *
 * Compact sur mobile, expanded sur desktop.
 *
 * Props:
 *   - handled, total, goal, remaining, sales, callbacksDueToday : chiffres
 *   - callStartAt: number|null — timestamp de l'appel en cours (pour timer)
 *   - onStopCall: () => void
 *   - sessionStartAt: number|null — début de la session pour calculer appels/h
 */
export default function SessionHUD({
  handled = 0,
  total = 0,
  goal = 0,
  sales = 0,
  callbacksDueToday = 0,
  callStartAt = null,
  onStopCall,
  sessionStartAt = null,
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 30000); // tick every 30s
    return () => clearInterval(iv);
  }, []);

  const goalPct = goal > 0 ? Math.min(100, Math.round((handled / goal) * 100)) : 0;

  // Appels / heure — basé sur début de session
  const callsPerHour = useMemo(() => {
    if (!sessionStartAt || handled === 0) return null;
    const hoursElapsed = Math.max(0.05, (now - sessionStartAt) / 3_600_000);
    return Math.round(handled / hoursElapsed);
  }, [sessionStartAt, handled, now]);

  const reachedGoal = goal > 0 && handled >= goal;

  return (
    <div
      className="sticky top-0 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-2 pb-2 bg-background/95 backdrop-blur-sm border-b border-border shadow-sm"
      data-testid="session-hud"
    >
      <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
        {/* Progress principal */}
        <div className="flex-1 min-w-[160px]">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
              <Target size={11} weight="duotone"/> Aujourd'hui
            </div>
            {reachedGoal && (
              <span className="text-[10px] font-bold text-primary" data-testid="hud-goal-reached">🎯 Objectif !</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${reachedGoal ? "bg-primary" : "bg-gradient-to-r from-primary/70 to-primary"}`}
                style={{ width: `${goalPct}%` }}
                data-testid="hud-progress-bar"
              />
            </div>
            <div className="text-sm font-bold tabular-nums text-foreground shrink-0" data-testid="hud-progress-count">
              {handled}<span className="text-muted-foreground font-normal"> / {goal || total}</span>
            </div>
          </div>
        </div>

        {/* Chips info compactes */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {callbacksDueToday > 0 && (
            <Chip
              icon={<Clock size={11} weight="fill"/>}
              label={`${callbacksDueToday} rappel${callbacksDueToday > 1 ? "s" : ""}`}
              color="secondary"
              testid="hud-callbacks"
            />
          )}
          {sales > 0 && (
            <Chip
              icon={<Trophy size={11} weight="fill"/>}
              label={`${sales} vente${sales > 1 ? "s" : ""} 🎉`}
              color="primary-solid"
              testid="hud-sales"
            />
          )}
          {callsPerHour !== null && callsPerHour > 0 && (
            <Chip
              icon={<Fire size={11} weight={callsPerHour >= 5 ? "fill" : "duotone"}/>}
              label={`${callsPerHour}/h`}
              color={callsPerHour >= 5 ? "primary-solid" : "muted"}
              testid="hud-calls-per-hour"
              title="Cadence d'appels par heure"
            />
          )}
          {callStartAt && (
            <CallTimer startAt={callStartAt} onStop={onStopCall} compact/>
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({ icon, label, color = "muted", testid, title }) {
  const cls = {
    "primary-solid": "bg-primary text-primary-foreground border-primary",
    "secondary": "bg-secondary/15 text-secondary border-secondary/40",
    "muted": "bg-muted/70 text-foreground border-border",
  }[color] || "bg-muted/70 text-foreground border-border";
  return (
    <span
      className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ${cls}`}
      data-testid={testid}
      title={title}
    >
      {icon} {label}
    </span>
  );
}
